import React, { useEffect, useRef, useCallback, useState } from 'react';
import styles from './ScrimYoutubePlayer.module.css';
import ScrimSyncOverlay from './ScrimSyncOverlay';

// YouTube Player API types
declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

export interface ScrimYoutubePlayerProps {
  videoId: string;
  startSeconds: number;
  shouldPlay: boolean;
  isMaster: boolean;
  isThumb: boolean;
  viewMode: 'grid' | 'single';
  title: string; // Add title prop
  trackIndex?: number; // Track index for hotkey display
  onReady: () => void;
  onPlay: () => void;
  onPause: () => void;
  onTimeUpdate: (time: number) => void;
  onClick?: () => void; // New onClick callback for thumbnail mode
  quality: 'hd1080' | 'hd720';
  onRequestPlay?: () => void;
  allReady?: boolean;
  volume: number; // Add volume prop
  onTrackSyncChange?: (videoId: string, offsetChange: number) => void; // Sync change callback
}

const ScrimYoutubePlayer: React.FC<ScrimYoutubePlayerProps> = ({
  videoId,
  startSeconds,
  shouldPlay,
  isMaster,
  isThumb,
  viewMode,
  title,
  trackIndex,
  onReady,
  onPlay,
  onPause,
  onTimeUpdate,
  onClick,
  quality,
  onRequestPlay,
  allReady,
  volume,
  onTrackSyncChange
}) => {
  const playerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const timeUpdateIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const autoplayTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastShouldPlayRef = useRef<boolean>(false);
  const isPlayerReadyRef = useRef<boolean>(false);
  const autoplayHandledRef = useRef<boolean>(false);
  const userHasPlayedRef = useRef<boolean>(false);
  const [playerStatus, setPlayerStatus] = React.useState<string>('initializing');
  const [playerError, setPlayerError] = React.useState<string | null>(null);
  const [scale, setScale] = React.useState<number>(1);
  const [isHovered, setIsHovered] = useState(false);

  // Compute target YouTube player dimensions based on selected quality
  const TARGET_WIDTH = quality === 'hd1080' ? 1920 : 1280;
  const TARGET_HEIGHT = quality === 'hd1080' ? 1080 : 720;

  // Utility function to trim title
  const trimTitle = (title: string): string => {
    if (title.length <= 24) return title;
    return title.substring(0, 24);
  };

  // Calculate scale factor based on desired display size
  React.useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const calculateScale = () => {
      // Clear any pending calculations
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      // Debounce the calculation slightly to avoid excessive calls
      timeoutId = setTimeout(() => {
        if (wrapperRef.current) {
          const wrapperWidth = wrapperRef.current.clientWidth;
          const wrapperHeight = wrapperRef.current.clientHeight;
          
          // Only calculate if we have valid dimensions
          if (wrapperWidth > 0 && wrapperHeight > 0) {
            // Calculate scale to fit the wrapper while maintaining aspect ratio
            const scaleX = wrapperWidth / TARGET_WIDTH;
            const scaleY = wrapperHeight / TARGET_HEIGHT;
            const newScale = Math.min(scaleX, scaleY, 1); // Don't scale up beyond 1:1
            
            // Only update if scale actually changed to avoid unnecessary re-renders
            setScale(prevScale => {
              if (Math.abs(prevScale - newScale) > 0.001) {
                return newScale;
              }
              return prevScale;
            });
          }
        }
      }, 10); // Small debounce to avoid excessive calculations
    };

    calculateScale();
    
    // Listen for window resize
    window.addEventListener('resize', calculateScale);
    
    // Use ResizeObserver for more precise container size changes
    const resizeObserver = new ResizeObserver(calculateScale);
    if (wrapperRef.current) {
      resizeObserver.observe(wrapperRef.current);
    }
    
    // Also trigger when the wrapper ref changes
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      window.removeEventListener('resize', calculateScale);
      resizeObserver.disconnect();
    };
  }, [isThumb, TARGET_WIDTH, TARGET_HEIGHT]); // Re-run when isThumb or target size changes
  
  // Ensure scale is recalculated after mount and layout changes
  React.useLayoutEffect(() => {
    const recalculateScale = () => {
      if (wrapperRef.current) {
        const wrapperWidth = wrapperRef.current.clientWidth;
        const wrapperHeight = wrapperRef.current.clientHeight;
        
        if (wrapperWidth > 0 && wrapperHeight > 0) {
          const scaleX = wrapperWidth / TARGET_WIDTH;
          const scaleY = wrapperHeight / TARGET_HEIGHT;
          const newScale = Math.min(scaleX, scaleY, 1);
          setScale(newScale);
        }
      }
    };
    
    // Immediate calculation
    recalculateScale();
    
    // Also trigger after a short delay to catch any layout changes
    const timer = setTimeout(recalculateScale, 100);
    
    return () => clearTimeout(timer);
  }, [videoId, isThumb, TARGET_WIDTH, TARGET_HEIGHT]); // Re-run when video, thumb mode, or target size changes
  
  // Additional effect to handle scale recalculation when container moves between positions
  React.useEffect(() => {
    const handlePositionChange = () => {
      // Immediate recalculation for snappy transitions
      if (wrapperRef.current) {
        const wrapperWidth = wrapperRef.current.clientWidth;
        const wrapperHeight = wrapperRef.current.clientHeight;
        
        if (wrapperWidth > 0 && wrapperHeight > 0) {
          const scaleX = wrapperWidth / TARGET_WIDTH;
          const scaleY = wrapperHeight / TARGET_HEIGHT;
          const newScale = Math.min(scaleX, scaleY, 1);
          setScale(newScale);
        }
      }
    };
    
    // Immediate execution for snappy transitions
    handlePositionChange();
    
    // Also trigger after a very short delay to ensure layout is updated
    const timer = setTimeout(handlePositionChange, 10);
    
    return () => clearTimeout(timer);
  }, [isThumb, TARGET_WIDTH, TARGET_HEIGHT]); // Trigger when thumbnail mode or target size changes

  // Load YouTube IFrame API
  useEffect(() => {
    const loadAPI = () => {
      if (!window.YT) {
        // Check if script is already being loaded
        if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
          const tag = document.createElement('script');
          tag.src = 'https://www.youtube.com/iframe_api';
          const firstScriptTag = document.getElementsByTagName('script')[0];
          firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
        }

        // Set up multiple callbacks handling
        const originalCallback = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
          if (originalCallback) originalCallback();
          initializePlayer();
        };
      } else if (window.YT.Player) {
        // API is already loaded
        initializePlayer();
      } else {
        // API is loading, wait for it
        const checkAPI = () => {
          if (window.YT && window.YT.Player) {
            initializePlayer();
          } else {
            setTimeout(checkAPI, 100);
          }
        };
        checkAPI();
      }
    };

    loadAPI();
  }, []);

  const initializePlayer = useCallback(() => {
    if (!containerRef.current || playerRef.current) return;

    setPlayerStatus('loading');
    setPlayerError(null);
    autoplayHandledRef.current = false;
    userHasPlayedRef.current = false;

    try {
      // Always use selected dimensions to control quality target
      playerRef.current = new window.YT.Player(containerRef.current, {
        height: TARGET_HEIGHT,
        width: TARGET_WIDTH,
        videoId: videoId,
        playerVars: {
          autoplay: 1, // Enable autoplay - we'll handle pausing it
          controls: 0, // Hide controls initially until user manually plays
          disablekb: 1, // Disable keyboard controls
          fs: isThumb ? 0 : 0, // Disable fullscreen button (especially for thumbnails)
          iv_load_policy: 3, // Hide annotations
          modestbranding: 1, // Hide YouTube logo
          rel: 0, // Don't show related videos
          showinfo: 0, // Hide video info
          start: 0, // Start at beginning, we'll seek later
          enablejsapi: 1, // Enable JS API
          playsinline: isThumb ? 1 : 0, // Play inline for thumbnails
          vq: quality, // Force selected quality
        },
        events: {
          onReady: handlePlayerReady,
          onStateChange: handlePlayerStateChange,
          onError: handlePlayerError,
        },
      });
    } catch (error) {
      console.error(`Error creating YouTube player for ${videoId}:`, error);
      setPlayerError(`Failed to create player: ${error}`);
      setPlayerStatus('error');
    }
  }, [videoId, isThumb, TARGET_WIDTH, TARGET_HEIGHT, quality]);

  const handlePlayerReady = useCallback(() => {
    isPlayerReadyRef.current = true;
    setPlayerStatus('waiting_autoplay');
    setPlayerError(null);
    
    // Set timeout fallback in case autoplay doesn't happen
    autoplayTimeoutRef.current = setTimeout(() => {
      if (!autoplayHandledRef.current) {
        autoplayHandledRef.current = true;
        setPlayerStatus('ready');
        onReady();
      }
    }, 3000); // 3 second timeout
    
    // Mute non-master players to prevent audio conflicts
    if (playerRef.current) {
      try {
        if (isMaster) {
          playerRef.current.unMute();
        } else {
          playerRef.current.mute();
        }
      } catch (error) {
        // Ignore mute errors
      }
    }
    
    // Start time update interval only for master player
    if (isMaster) {
      timeUpdateIntervalRef.current = setInterval(() => {
        if (playerRef.current && isPlayerReadyRef.current) {
          try {
            const currentTime = playerRef.current.getCurrentTime();
            onTimeUpdate(currentTime);
          } catch (error) {
            // Ignore errors when player is not ready
          }
        }
      }, 100); // Update every 100ms for smooth sync
    }
  }, [onTimeUpdate, videoId, isMaster, onReady]);

  const handlePlayerStateChange = useCallback((event: any) => {
    const state = event.data;
    
    switch (state) {
      case -1: // YT.PlayerState.UNSTARTED
        break;
        
      case 0: // YT.PlayerState.ENDED
        onPause();
        break;
        
      case 1: // YT.PlayerState.PLAYING
        if (!autoplayHandledRef.current) {
          // This is the autoplay we need to handle
          setTimeout(() => {
            if (playerRef.current) {
              playerRef.current.pauseVideo();
              autoplayHandledRef.current = true;
              setPlayerStatus('ready');
              onReady(); // Now we're truly ready for user interaction
              
              // Clear the autoplay timeout since we handled it
              if (autoplayTimeoutRef.current) {
                clearTimeout(autoplayTimeoutRef.current);
                autoplayTimeoutRef.current = null;
              }
            }
          }, 100); // Small delay to ensure the play state is registered
        } else if (userHasPlayedRef.current) {
          // This is user-initiated play
          onPlay();
        }
        break;
        
      case 2: // YT.PlayerState.PAUSED
        if (autoplayHandledRef.current) {
          onPause();
        }
        break;
        
      case 3: // YT.PlayerState.BUFFERING
        // Don't change play state during buffering
        break;
        
      case 5: // YT.PlayerState.CUED
        break;
        
      default:
        console.log(`Unknown player state ${state} for ${videoId}`);
        break;
    }
  }, [onPlay, onPause, onReady, videoId]);

  const handlePlayerError = useCallback((event: any) => {
    console.error(`YouTube player error for ${videoId}:`, event.data);
    let errorMessage = 'Unknown error';
    switch (event.data) {
      case 2:
        errorMessage = 'Invalid video ID';
        break;
      case 5:
        errorMessage = 'HTML5 player error';
        break;
      case 100:
        errorMessage = 'Video not found or private';
        break;
      case 101:
      case 150:
        errorMessage = 'Video not allowed in embedded players';
        break;
    }
    setPlayerError(errorMessage);
    setPlayerStatus('error');
  }, [videoId]);

  // Handle play/pause changes
  useEffect(() => {
    if (!playerRef.current || !isPlayerReadyRef.current || !autoplayHandledRef.current) return;

    const wasPlaying = lastShouldPlayRef.current;
    const shouldNowPlay = shouldPlay;

    if (wasPlaying !== shouldNowPlay) {
      try {
        if (shouldNowPlay) {
          // User wants to play - mark that user has played and enable controls
          userHasPlayedRef.current = true;
          setPlayerStatus('playing');
          
          // Enable controls now that user is actively using the player
          const iframe = containerRef.current?.querySelector('iframe');
          if (iframe) {
            const src = iframe.src;
            const newSrc = src.includes('controls=0') 
              ? src.replace('controls=0', 'controls=1')
              : src + (src.includes('?') ? '&' : '?') + 'controls=1';
            iframe.src = newSrc;
          }
          
          // Seek to the correct time and play
          playerRef.current.seekTo(startSeconds, true);
          playerRef.current.playVideo();
        } else {
          // Pause the video
          playerRef.current.pauseVideo();
          setPlayerStatus('ready');
        }
      } catch (error) {
        console.warn('Error controlling YouTube player:', error);
      }
    }
    
    lastShouldPlayRef.current = shouldNowPlay;
  }, [shouldPlay, startSeconds]);

  // Handle time seeking
  useEffect(() => {
    if (!playerRef.current || !isPlayerReadyRef.current || !autoplayHandledRef.current) return;

    try {
      const currentTime = playerRef.current.getCurrentTime();
      const timeDiff = Math.abs(currentTime - startSeconds);
      
      // Be more aggressive about seeking - always seek if difference is more than 0.5 seconds
      // Also seek if we're paused (to allow timeline scrubbing)
      if (timeDiff > 0.5) {
        playerRef.current.seekTo(startSeconds, true);
      }
    } catch (error) {
      // Ignore seek errors
      console.warn(`Seek error for ${videoId}:`, error);
    }
  }, [startSeconds, shouldPlay, videoId]);

  // Handle mute/unmute when master status changes
  useEffect(() => {
    if (!playerRef.current || !isPlayerReadyRef.current || !autoplayHandledRef.current) return;

    try {
      if (isMaster) {
        playerRef.current.unMute();
      } else {
        playerRef.current.mute();
      }
    } catch (error) {
      // Ignore mute/unmute errors
    }
  }, [isMaster]);

  // Ensure time updates follow the current master player
  useEffect(() => {
    if (!isPlayerReadyRef.current || !autoplayHandledRef.current) return;

    // Always clear any existing interval first
    if (timeUpdateIntervalRef.current) {
      clearInterval(timeUpdateIntervalRef.current);
      timeUpdateIntervalRef.current = null;
    }

    if (isMaster && playerRef.current) {
      timeUpdateIntervalRef.current = setInterval(() => {
        try {
          const currentTime = playerRef.current.getCurrentTime();
          onTimeUpdate(currentTime);
        } catch (error) {
          // Ignore errors when player is not ready
        }
      }, 100);
    }

    return () => {
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
        timeUpdateIntervalRef.current = null;
      }
    };
  }, [isMaster, onTimeUpdate]);

  // Handle volume changes
  useEffect(() => {
    if (!playerRef.current || !isPlayerReadyRef.current || !autoplayHandledRef.current) return;

    try {
      playerRef.current.setVolume(volume);
    } catch (error) {
      // Ignore volume errors
    }
  }, [volume]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
      }
      if (autoplayTimeoutRef.current) {
        clearTimeout(autoplayTimeoutRef.current);
      }
      if (playerRef.current && playerRef.current.destroy) {
        try {
          playerRef.current.destroy();
        } catch (error) {
          // Ignore destruction errors
        }
      }
    };
  }, []);

  const showLoadingOverlay = !isThumb && (
                            playerStatus === 'initializing' || 
                            playerStatus === 'loading' || 
                            playerStatus === 'waiting_autoplay'
                          );

  const showBufferingOverlay = false; // Buffering overlay removed with debug functionality

  const canClickToPlay = !!onRequestPlay && !!allReady && isMaster && !isThumb && (playerStatus === 'ready');

  const handleSyncChange = (offsetChange: number) => {
    if (onTrackSyncChange) {
      onTrackSyncChange(videoId, offsetChange);
    }
  };

  // Compute dynamic overlay placement in grid mode: align to bottom of actual video area
  const overlayStyle: React.CSSProperties | undefined = React.useMemo(() => {
    if (viewMode !== 'grid') return undefined;
    // Estimated overlay height in px (should match CSS height)
    const overlayHeight = 60;
    const videoBottomOffsetFromCenter = (TARGET_HEIGHT * scale) / 2;
    const translateY = videoBottomOffsetFromCenter - overlayHeight;
    return {
      top: '50%',
      bottom: 'auto',
      transform: `translateY(${translateY}px)`,
    } as React.CSSProperties;
  }, [viewMode, TARGET_HEIGHT, scale]);

  return (
    <div 
      className={`${styles.container} ${isThumb ? styles.thumbnail : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Title bar - only show for thumbnails in single mode */}
      {viewMode === 'single' && isThumb && (
        <div className={styles.titleBar}>
          <div className={styles.titleContent}>
            <div className={styles.titleIndex}>
              {trackIndex !== undefined ? (trackIndex + 1) : ''}
            </div>
            <div className={styles.titleText}>
              {trimTitle(title)}
            </div>
          </div>
        </div>
      )}
      
      <div 
        className={styles.playerWrapper} 
        ref={wrapperRef}
      >
        <div 
          className={styles.scalingContainer}
          style={{
            transform: `translate(-50%, -50%) scale(${scale})`,
            transformOrigin: 'center',
            width: `${TARGET_WIDTH}px`,
            height: `${TARGET_HEIGHT}px`,
            position: 'absolute',
            top: '50%',
            left: '50%',
            willChange: 'transform',
          }}
        >
          <div 
            ref={containerRef} 
            className={styles.player}
            id={`youtube-player-${videoId}`}
            style={{
              width: '100%',
              height: '100%',
            }}
          />
        </div>
        {showLoadingOverlay && (
          <div className={`${styles.loadingOverlay} ${canClickToPlay ? styles.clickableOverlay : ''}`} onClick={canClickToPlay ? onRequestPlay : undefined}>
            <div className={`${styles.loadingBadge} ${isMaster && !isThumb ? styles.loadingBadgeLarge : ''} ${canClickToPlay ? styles.loadingBadgeClickable : ''}`}>
              <div className={`${styles.loadingText} ${isMaster && !isThumb ? styles.loadingTextLarge : ''}`}>
                {(playerStatus === 'initializing') && (
                  <>Initializing...</>
                )}
                {(playerStatus === 'loading') && (
                  <>
                    <span className={styles.inlineSpinner} />
                    <span>Loading video...</span>
                  </>
                )}
                {(playerStatus === 'waiting_autoplay') && (
                  <>
                    <span className={styles.inlineSpinner} />
                    <span>Preparing...</span>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        {showBufferingOverlay && (
          <div className={styles.bufferingOverlay}>
            <div className={styles.bufferingContent}>
              <div className={styles.bufferingSpinner}></div>
              <div className={styles.bufferingText}>Seeking...</div>
            </div>
          </div>
        )}
        {playerStatus === 'error' && (
          <div className={styles.errorOverlay}>
            <div className={styles.errorText}>
              Failed to load video
              {playerError && <div className={styles.errorDetails}>{playerError}</div>}
            </div>
          </div>
        )}
        {/* Click overlay for thumbnail mode */}
        {isThumb && onClick && (
          <div className={styles.clickOverlay} onClick={onClick}>
            {/* Transparent overlay to capture clicks in thumbnail mode */}
          </div>
        )}
        {/* Sync overlay - only show for non-thumbnail, non-master players */}
        {!isThumb && !isMaster && onTrackSyncChange && (
          <ScrimSyncOverlay
            onSyncChange={handleSyncChange}
            isVisible={isHovered}
            containerStyle={overlayStyle}
          />
        )}
        {/* Debug overlay removed */}
      </div>
    </div>
  );
};

export default ScrimYoutubePlayer; 